import type { SubscriptionMarket, SubscriptionMarketCatalog } from '../types/payment.types';

const subscriptionMarketStorageKey = 'subscription_market';

const findMarket = (markets: SubscriptionMarket[], value: string | null | undefined) => {
    const normalized = value?.trim();
    return normalized
        ? markets.find((market) => market.code.toLowerCase() === normalized.toLowerCase())
        : undefined;
};

const readStoredMarketCode = (): string | null => {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(subscriptionMarketStorageKey);
    } catch {
        return null;
    }
};

const getBrowserCountryCodes = (): string[] => {
    if (typeof navigator === 'undefined') return [];
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    return languages.flatMap((language) => {
        const parts = language.replace(/_/g, '-').split('-');
        const region = [...parts].reverse().find((part) => /^[A-Za-z]{2}$/.test(part));
        return region ? [region.toUpperCase()] : [];
    });
};

const getBrowserTimeZone = (): string | null => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
};

export const resolveSubscriptionMarket = (
    catalog: SubscriptionMarketCatalog,
    requestedMarketCode?: string | null
): SubscriptionMarket | null => {
    const requestedMarket = findMarket(catalog.markets, requestedMarketCode);
    if (requestedMarket) return requestedMarket;

    const storedMarket = findMarket(catalog.markets, readStoredMarketCode());
    if (storedMarket) return storedMarket;

    const detectedMarket = findMarket(catalog.markets, catalog.detectedMarketCode);
    if (detectedMarket) return detectedMarket;

    const browserCountryCodes = getBrowserCountryCodes();
    const browserCountryMarket = browserCountryCodes
        .map((browserCountryCode) => catalog.markets.find((market) => market.countryCodes.some(
            (countryCode) => countryCode.toUpperCase() === browserCountryCode
        )))
        .find((market) => market !== undefined);
    if (browserCountryMarket) return browserCountryMarket;

    const browserTimeZone = getBrowserTimeZone();
    const browserTimeZoneMarket = browserTimeZone
        ? catalog.markets.find((market) => market.timeZones.includes(browserTimeZone))
        : undefined;
    if (browserTimeZoneMarket) return browserTimeZoneMarket;

    return findMarket(catalog.markets, catalog.defaultMarketCode) ?? catalog.markets[0] ?? null;
};

export const storeSubscriptionMarket = (marketCode: string) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(subscriptionMarketStorageKey, marketCode);
    } catch {
        // Region selection remains usable when browser storage is unavailable.
    }
};
